/**
 * RealSqliteDatabase — Implementation of SqliteDatabase interface (§93 §2)
 *
 * Wires the high-level SqliteDatabase/SqliteConnection API to actual
 * sqlite3.wasm instances. Phase 4: multi-connection concurrency.
 *
 * Architecture:
 *   openDatabase() → RealSqliteDatabase (owns SABs)
 *     ├─ connect() → WorkerThreadConnection (shared SABs, separate thread)
 *     └─ connect() → InProcessConnection (shared SABs, same thread fallback)
 *
 * Browser note: this module is the same-thread runtime. Browser UI-thread
 * callers must not use it in production; `session.openSqlite()` is expected
 * to route browser opens through the dedicated worker proxy path instead.
 *
 * Non-browser note: sqlite3mt attachment-capable factories use worker_threads
 * for public client connections. Other factories still fall back to
 * same-thread connections.
 *
 * All connections share the same SABs (sab1, sab2, sab3, control).
 * WAL concurrency: N readers + 1 writer via xShmLock on SAB₃.
 * Snapshot isolation: each BEGIN sees committed state at that moment.
 *
 * §93 §13: Full observability — diagnostics() reads live SAB/VFS state,
 * onDiagnostics() fires at max 10Hz on change.
 */

import type {
    SqliteDatabase,
    SqliteConnection,
    SqliteCloseOptions,
    SqliteTransaction,
    SqliteStatementResult,
    ConnectionOptions,
    ExecResult,
    QueryOptions,
    TransactionOptions,
    SyncMode,
    SyncResult,
    SyncEvent,
    SyncConfig,
    DatabaseConfig,
    SqlParam,
    PreparedStatement,
    MergeConflict,
    ConnectionDiag,
    SyncDiag,
    DatabaseDiagnostics,
    SqliteFlushLocalOptions,
} from './sqliteDatabase'
import { SqliteError } from './sqliteDatabase'
import type {
    BrowserSqliteClientToControlMessage,
    BrowserSqliteClientWorkerRequest,
    BrowserSqliteWorkerResponse,
    SerializedSqliteError,
} from './browserSqliteProtocol'
import {
    dispatchSqlite3MtExternalMessage,
    enterSqlite3MtExternalClientRequest,
    enterSqlite3MtExternalClientIoFatal,
    leaveSqlite3MtExternalClientRequest,
    releaseSqlite3MtClientAttachment,
    reserveSqlite3MtClientAttachment,
    toSqliteEngineOpenConfig,
    type SqliteEngineDatabaseAdapter,
    type SqliteEngineFactory,
    type SqliteEngineHandle,
} from './sqliteEngine'
import { WAL_FRAME_HDR_SIZE, WAL_HDR_SIZE } from './sqlite3mt/wal-checksum'
import { SHM_DATA_SIZE as WALINDEX_PGSZ, sab2MaxByteLength } from './sqlite3mt/sab-layout'
import { sdkConsole } from './logger'
import { createManagedWalCheckpointResult } from './sqliteWalCheckpointPragma'
import { SQLITE3MT_CLIENT_BUSY_RETRY_WINDOW_MS } from './sqliteBusyPolicy'
import { isBusyLikeSqliteError, recoverAfterFailedCommit } from './sqliteTransactionRecovery'
import type {
    MessageChannel as NodeMessageChannel,
    MessagePort as NodeMessagePort,
    Worker as NodeWorker,
} from 'node:worker_threads'

const CONNECTION_CLOSE_GRACE_MS = 500
const SQLITE_BUSY_RETRY_MIN_DELAY_MS = 20
const SQLITE_BUSY_RETRY_MAX_DELAY_MS = 150

function readStatementMetadata(handle: SqliteEngineHandle): ExecResult {
    const changes = handle.query<{ c: number }>('SELECT changes() as c')[0]?.c ?? 0
    const lastId = handle.query<{ id: number }>('SELECT last_insert_rowid() as id')[0]?.id ?? 0
    return {
        changes,
        lastInsertRowId: BigInt(lastId),
    }
}

type ExplicitTransactionState =
    | { inTransaction: true; transactionType: 'deferred' | 'immediate' }
    | { inTransaction: false; transactionType: undefined }

type Sqlite3MtDispatchableClientMessage = Extract<
    BrowserSqliteClientToControlMessage,
    { type: 'fault' | 'wal-commit-sync' }
>

function logSqlStatement(args: {
    connId?: number
    label?: string
    sql: string
    paramsCount: number
    durationMs: number
    rowCount?: number
    changes?: number
    ok: boolean
    error?: unknown
    txId?: number
}): void {
    sdkConsole.debug('[sqlite3mt:sql] statement', {
        ...(args.connId != null ? { connId: args.connId } : {}),
        ...(args.label != null ? { label: args.label } : {}),
        ...(args.txId != null ? { txId: args.txId } : {}),
        sql: args.sql,
        paramsCount: args.paramsCount,
        durationMs: Math.round(args.durationMs),
        ...(args.rowCount != null ? { rowCount: args.rowCount } : {}),
        ...(args.changes != null ? { changes: args.changes } : {}),
        ok: args.ok,
        ...(args.error != null
            ? { error: args.error instanceof Error ? args.error.message : String(args.error) }
            : {}),
    })
}

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => {
        setTimeout(resolve, ms)
    })
}

async function withBusyRetry<T>(action: () => T | Promise<T>): Promise<T> {
    const startedAt = performance.now()
    let attempt = 0

    for (;;) {
        try {
            return await action()
        } catch (error) {
            if (
                !isBusyLikeSqliteError(error)
                || performance.now() - startedAt >= SQLITE3MT_CLIENT_BUSY_RETRY_WINDOW_MS
            ) {
                throw error
            }

            const delayMs = Math.min(
                SQLITE_BUSY_RETRY_MIN_DELAY_MS * (attempt < 3 ? 1 << attempt : 8),
                SQLITE_BUSY_RETRY_MAX_DELAY_MS,
            )
            attempt += 1
            await sleep(delayMs)
        }
    }
}

function getExplicitTransactionState(sql: string): ExplicitTransactionState | null {
    const normalized = sql.trim().replace(/;+$/, '').replace(/\s+/g, ' ').toUpperCase()
    if (!normalized) {
        return null
    }

    const beginMatch = normalized.match(/^BEGIN(?: (DEFERRED|IMMEDIATE|EXCLUSIVE))?(?: TRANSACTION)?$/)
    if (beginMatch) {
        return {
            inTransaction: true,
            transactionType: beginMatch[1] === 'IMMEDIATE' || beginMatch[1] === 'EXCLUSIVE'
                ? 'immediate'
                : 'deferred',
        }
    }

    if (/^(?:COMMIT|END)(?: TRANSACTION)?$/.test(normalized) || /^ROLLBACK(?: TRANSACTION)?$/.test(normalized)) {
        return {
            inTransaction: false,
            transactionType: undefined,
        }
    }

    return null
}

function isSqlite3MtDispatchableClientMessage(
    message: BrowserSqliteClientToControlMessage
): message is Sqlite3MtDispatchableClientMessage {
    return message.type === 'fault' || message.type === 'wal-commit-sync'
}

interface PendingRequest {
    resolve(value: unknown): void
    reject(reason: unknown): void
}

type RpcPayload<TRequest> =
    TRequest extends { kind: 'request'; requestId: number }
        ? Omit<TRequest, 'kind' | 'requestId'>
        : never

interface NodeWorkerThreadsModule {
    Worker: typeof NodeWorker
    MessageChannel: typeof NodeMessageChannel
}

interface NodeWorkerOptions {
    execArgv?: string[]
}

const NODE_WORKER_THREADS_SPECIFIER = 'node:worker_threads'
const EXTERNAL_ATTACHMENT_UNSUPPORTED_ERROR = 'sqlite3mt engine factory does not support external client attachments'
const NODE_TS_MODULE_PATTERN = /\.(?:cts|mts|ts|tsx)$/i
const COMPONENT_NODE_SQLITE_WORKER_PATH_ENV = 'SECUREBLOB_NODE_SQLITE_WORKER_PATH'

let cachedNodeWorkerThreadsPromise: Promise<NodeWorkerThreadsModule | null> | null = null

function createConnectionDiag(label: string): ConnectionDiag {
    return {
        label,
        isOpen: true,
        inTransaction: false,
        transactionType: undefined,
        preparedStatementCount: 0,
        lastOperationMs: 0,
        totalQueries: 0,
        totalWrites: 0,
    }
}

function deserializeError(error: SerializedSqliteError): Error {
    if (typeof error.code === 'number') {
        const sqliteError = new SqliteError(error.message, error.code, error.codeName ?? `SQLITE_${error.code}`)
        if (error.stack) sqliteError.stack = error.stack
        return sqliteError
    }

    const err = new Error(error.message)
    err.name = error.name
    if (error.stack) err.stack = error.stack
    return err
}

function isWorkerResponse(message: unknown): message is BrowserSqliteWorkerResponse {
    return !!message
        && typeof message === 'object'
        && 'kind' in message
        && (message as { kind?: unknown }).kind === 'response'
        && 'requestId' in message
        && 'ok' in message
}

function isExternalAttachmentUnsupportedError(error: unknown): boolean {
    return error instanceof Error && error.message === EXTERNAL_ATTACHMENT_UNSUPPORTED_ERROR
}

function isNodeLikeRuntime(): boolean {
    const nodeGlobal = globalThis as typeof globalThis & {
        process?: {
            versions?: {
                node?: string
            }
        }
    }

    return typeof nodeGlobal.process?.versions?.node === 'string'
}

async function loadNodeWorkerThreads(): Promise<NodeWorkerThreadsModule | null> {
    if (!isNodeLikeRuntime()) {
        return null
    }
    const disableWorkerThreads = globalThis.process?.env?.SECUREBLOB_DISABLE_NODE_SQLITE_WORKER_THREADS
    if (disableWorkerThreads === '1' || disableWorkerThreads?.toLowerCase() === 'true') {
        return null
    }

    cachedNodeWorkerThreadsPromise ??= import(
        /* @vite-ignore */ NODE_WORKER_THREADS_SPECIFIER
    ).then(module => module as NodeWorkerThreadsModule).catch(() => null)

    return await cachedNodeWorkerThreadsPromise
}

function resolveNodeTsxRegisterSpecifier(): string {
    try {
        const resolver = (import.meta as ImportMeta & {
            resolve?: (specifier: string) => string
        }).resolve
        const resolved = resolver?.('tsx/esm/api')
        if (typeof resolved === 'string' && resolved.length > 0 && resolved.includes(':')) {
            return resolved
        }
    } catch {
        // Fall back to bare package resolution when import.meta.resolve is unavailable.
    }

    return new URL('../../../node_modules/tsx/dist/esm/api/index.mjs', import.meta.url).href
}

function buildNodeTsWorkerImportArg(): string {
    const registerScript = [
        `import { register } from ${JSON.stringify(resolveNodeTsxRegisterSpecifier())};`,
        'register();',
    ].join(' ')
    return `data:text/javascript,${encodeURIComponent(registerScript)}`
}

function buildNodeWorkerOptions(workerUrl: URL): NodeWorkerOptions | undefined {
    if (!NODE_TS_MODULE_PATTERN.test(workerUrl.pathname)) {
        return undefined
    }

    return {
        // Worker threads need a TS-aware preload before the source entrypoint is
        // evaluated. Use tsx's register API rather than raw loader wiring.
        execArgv: [`--import=${buildNodeTsWorkerImportArg()}`],
    }
}

function pathToFileUrl(workerPath: string): URL {
    const normalized = workerPath.replace(/\\/g, '/')
    const withLeadingSlash = normalized.startsWith('/') ? normalized : `/${normalized}`
    return new URL(`file://${withLeadingSlash.split('/').map(part => encodeURIComponent(part)).join('/')}`)
}

function resolveComponentNodeSqliteWorkerUrl(): URL | null {
    const workerPath = globalThis.process?.env?.[COMPONENT_NODE_SQLITE_WORKER_PATH_ENV]?.trim()
    if (!workerPath) {
        return null
    }

    return pathToFileUrl(workerPath)
}

class NodeWorkerRpc<TRequest extends { kind: 'request'; requestId: number }> {
    private nextRequestId = 1
    private readonly pending = new Map<number, PendingRequest>()
    private onFailure: ((error: Error) => void) | null = null
    private terminalError: Error | null = null
    private terminated = false

    constructor(private readonly worker: NodeWorker) {
        worker.on('message', (message: unknown) => {
            if (!isWorkerResponse(message)) {
                return
            }

            const pending = this.pending.get(message.requestId)
            if (!pending) {
                return
            }

            this.pending.delete(message.requestId)
            if (message.ok) {
                pending.resolve(message.result)
            } else {
                pending.reject(deserializeError(message.error))
            }
        })

        worker.on('error', error => {
            this.fail(error, true)
        })
        worker.on('messageerror', () => {
            this.fail(new Error('sqlite worker message deserialization failed'), true)
        })
        worker.on('exit', code => {
            if (!this.terminated && code !== 0) {
                this.fail(new Error(`sqlite worker exited with code ${code}`), true)
            }
        })
    }

    setFailureHandler(handler: (error: Error) => void): void {
        this.onFailure = handler
    }

    request<TResult>(
        request: RpcPayload<TRequest>,
        transfer: readonly unknown[] = [],
    ): Promise<TResult> {
        if (this.terminalError != null) {
            return Promise.reject(this.terminalError)
        }

        const requestId = this.nextRequestId++
        const message = {
            kind: 'request',
            requestId,
            ...request,
        } as unknown as TRequest

        return new Promise<TResult>((resolve, reject) => {
            this.pending.set(requestId, { resolve, reject })
            try {
                this.worker.postMessage(message, transfer as never[])
            } catch (error) {
                this.pending.delete(requestId)
                reject(error)
            }
        })
    }

    async terminate(): Promise<void> {
        if (this.terminated) {
            return
        }

        this.terminated = true
        const termination = this.worker.terminate().catch(() => {
            // Worker termination is best-effort during shutdown.
        })
        this.fail(new Error('sqlite worker terminated'), false)
        await termination
    }

    private fail(reason: unknown, notifyFailure: boolean): void {
        const error = reason instanceof Error ? reason : new Error(String(reason))
        if (this.terminalError == null) {
            this.terminalError = error
        }

        for (const pending of this.pending.values()) {
            pending.reject(this.terminalError)
        }
        this.pending.clear()

        if (notifyFailure) {
            this.onFailure?.(this.terminalError)
        }
    }
}

class WorkerThreadConnection implements SqliteConnection {
    private _isOpen = true
    private diag: ConnectionDiag
    private operationTail: Promise<void> = Promise.resolve()

    constructor(
        private readonly rpc: NodeWorkerRpc<BrowserSqliteClientWorkerRequest>,
        private readonly connId: number,
        initialDiag: ConnectionDiag,
        private readonly releaseResources: () => void,
        private readonly onClosed?: (conn: WorkerThreadConnection) => void,
    ) {
        this.diag = initialDiag
        this.rpc.setFailureHandler(() => {
            this.handleWorkerFailure()
        })
    }

    get isOpen(): boolean { return this._isOpen }

    get pageCount(): number {
        // WorkerThreadConnection does not have direct access to the database adapter.
        // The page count is available via the worker's diagnostics snapshot.
        // Returning 0 as a stub — the browser-sql-explorer surface uses InProcessConnection
        // for same-thread runtime, where pageCount is properly implemented.
        return 0
    }

    diagnostics(): ConnectionDiag {
        return this.diag
    }

    async execute<T = Record<string, unknown>>(
        sql: string,
        params?: SqlParam[],
        options?: QueryOptions,
    ): Promise<SqliteStatementResult<T>> {
        return this.enqueueConnectionOperation(async () => {
        this.assertOpen()
        options?.signal?.throwIfAborted()
        const t0 = performance.now()
        const managedCheckpoint = createManagedWalCheckpointResult<T>(sql, params)
        if (managedCheckpoint) {
            this.recordStatementMetrics(managedCheckpoint, t0)
            logSqlStatement({
                connId: this.connId,
                sql,
                paramsCount: params?.length ?? 0,
                durationMs: performance.now() - t0,
                rowCount: managedCheckpoint.rows.length,
                changes: managedCheckpoint.changes,
                ok: true,
            })
            return managedCheckpoint
        }

        try {
            const result = await this.rpc.request<SqliteStatementResult<T>>({
                op: 'conn-execute',
                connId: this.connId,
                sql,
                params,
            })
            this.recordStatementMetrics(result, t0)
            this.applyExplicitTransactionState(sql)
            logSqlStatement({
                connId: this.connId,
                sql,
                paramsCount: params?.length ?? 0,
                durationMs: performance.now() - t0,
                rowCount: result.rows.length,
                changes: result.changes,
                ok: true,
            })
            return result
        } catch (error) {
            this.recordStatementFailure(t0)
            logSqlStatement({
                connId: this.connId,
                sql,
                paramsCount: params?.length ?? 0,
                durationMs: performance.now() - t0,
                ok: false,
                error,
            })
            throw error
        }
        })
    }

    async query<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<T[]> {
        return (await this.execute<T>(sql, params, options)).rows
    }

    async exec(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<ExecResult> {
        const result = await this.execute(sql, params, options)
        if (result.changes === 0) {
            this.recordZeroChangeWrite()
        }
        return {
            changes: result.changes,
            lastInsertRowId: result.lastInsertRowId,
        }
    }

    async *each<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): AsyncIterable<T> {
        const rows = await this.query<T>(sql, params, options)
        for (const row of rows) {
            yield row
        }
    }

    async prepare<T = Record<string, unknown>>(sql: string): Promise<PreparedStatement<T>> {
        this.assertOpen()
        let finalized = false
        const ensureOpen = () => {
            if (finalized) throw new Error('Statement already finalized')
        }

        return {
            all: async (params?: SqlParam[]) => {
                ensureOpen()
                return this.query<T>(sql, params)
            },
            get: async (params?: SqlParam[]) => {
                ensureOpen()
                return (await this.query<T>(sql, params))[0] ?? null
            },
            run: async (params?: SqlParam[]) => {
                ensureOpen()
                return this.exec(sql, params)
            },
            finalize: async () => { finalized = true },
            [Symbol.asyncDispose]: async () => { finalized = true },
        }
    }

    async transaction<T>(fn: (tx: SqliteTransaction) => Promise<T>, options?: TransactionOptions): Promise<T> {
        return this.enqueueConnectionOperation(async () => {
        this.assertOpen()
        const txId = await this.rpc.request<number>({
            op: 'tx-begin',
            connId: this.connId,
            immediate: !!options?.immediate,
        })
        this.setDiag({
            ...this.diag,
            inTransaction: true,
            transactionType: options?.immediate ? 'immediate' : 'deferred',
        })
        const recordStatementMetrics = this.recordStatementMetrics.bind(this)
        const recordStatementFailure = this.recordStatementFailure.bind(this)
        const recordZeroChangeWrite = this.recordZeroChangeWrite.bind(this)
        const tx: SqliteTransaction = {
            execute: async <T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]) => {
                const t0 = performance.now()
                try {
                    const result = await this.rpc.request<SqliteStatementResult<T2>>({
                        op: 'tx-execute',
                        txId,
                        sql,
                        params,
                    })
                    recordStatementMetrics(result, t0)
                    logSqlStatement({
                        connId: this.connId,
                        txId,
                        sql,
                        paramsCount: params?.length ?? 0,
                        durationMs: performance.now() - t0,
                        rowCount: result.rows.length,
                        changes: result.changes,
                        ok: true,
                    })
                    return result
                } catch (error) {
                    recordStatementFailure(t0)
                    logSqlStatement({
                        connId: this.connId,
                        txId,
                        sql,
                        paramsCount: params?.length ?? 0,
                        durationMs: performance.now() - t0,
                        ok: false,
                        error,
                    })
                    throw error
                }
            },
            query: async <T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]) => {
                return (await tx.execute<T2>(sql, params)).rows
            },
            exec: async (sql: string, params?: SqlParam[]) => {
                const result = await tx.execute(sql, params)
                if (result.changes === 0) {
                    recordZeroChangeWrite()
                }
                return {
                    changes: result.changes,
                    lastInsertRowId: result.lastInsertRowId,
                }
            },
            each: async function* <T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]) {
                const rows = await tx.query<T2>(sql, params)
                for (const row of rows) yield row
            },
        }

        let commitAttempted = false
        try {
            const result = await fn(tx)
            commitAttempted = true
            await this.rpc.request<void>({ op: 'tx-commit', txId })
            return result
        } catch (error) {
            if (!commitAttempted) {
                try {
                    await this.rpc.request<void>({ op: 'tx-rollback', txId })
                } catch {
                    // rollback best-effort
                }
            }
            throw error
        } finally {
            this.setDiag({
                ...this.diag,
                inTransaction: false,
                transactionType: undefined,
            })
        }
        })
    }

    async close(): Promise<void> {
        if (!this._isOpen) return
        this._isOpen = false
        try {
            await Promise.race([
                this.rpc.request<void>({ op: 'conn-close', connId: this.connId }),
                new Promise<void>(resolve => {
                    setTimeout(resolve, CONNECTION_CLOSE_GRACE_MS)
                }),
            ])
        } finally {
            this.releaseResources()
            await this.rpc.terminate()
            this.setDiag({ ...this.diag, isOpen: false, inTransaction: false, transactionType: undefined })
            this.onClosed?.(this)
        }
    }

    async [Symbol.asyncDispose](): Promise<void> {
        await this.close()
    }

    private assertOpen(): void {
        if (!this._isOpen) throw new Error('Connection is closed')
    }

    private enqueueConnectionOperation<T>(operation: () => Promise<T>): Promise<T> {
        this.assertOpen()
        const previous = this.operationTail
        let release!: () => void
        this.operationTail = new Promise<void>(resolve => {
            release = resolve
        })
        return previous.catch(() => {}).then(async () => {
            try {
                this.assertOpen()
                return await operation()
            } finally {
                release()
            }
        })
    }

    private recordStatementMetrics(
        result: Pick<SqliteStatementResult, 'changes'>,
        startedAt: number,
    ): void {
        this.setDiag({
            ...this.diag,
            totalQueries: this.diag.totalQueries + 1,
            totalWrites: this.diag.totalWrites + (result.changes > 0 ? 1 : 0),
            lastOperationMs: performance.now() - startedAt,
        })
    }

    private recordZeroChangeWrite(): void {
        this.setDiag({
            ...this.diag,
            totalWrites: this.diag.totalWrites + 1,
        })
    }

    private recordStatementFailure(startedAt: number): void {
        this.setDiag({
            ...this.diag,
            lastOperationMs: performance.now() - startedAt,
        })
    }

    private applyExplicitTransactionState(sql: string): void {
        const nextState = getExplicitTransactionState(sql)
        if (!nextState) {
            return
        }

        this.setDiag({
            ...this.diag,
            inTransaction: nextState.inTransaction,
            transactionType: nextState.transactionType,
        })
    }

    private setDiag(diag: ConnectionDiag): void {
        this.diag = diag
    }

    private handleWorkerFailure(): void {
        if (!this._isOpen) {
            return
        }

        this._isOpen = false
        this.releaseResources()
        void this.rpc.terminate()
        this.setDiag({ ...this.diag, isOpen: false, inTransaction: false, transactionType: undefined })
        this.onClosed?.(this)
    }
}

// ── InProcessConnection ─────────────────────────────────────────

/**
 * SqliteConnection backed by an in-process SqliteHandle.
 * Same thread, same WASM instance. WAL locks coordinate via SAB₃.
 *
 * §93 §13.1: Per-connection instrumentation — query/write counts,
 * latency tracking, transaction state, prepared statement count.
 */
class InProcessConnection implements SqliteConnection {
    private _isOpen = true
    private handle: SqliteEngineHandle
    private _label: string
    private operationTail: Promise<void> = Promise.resolve()

    // §93 §13.1 ConnectionDiag instrumentation
    private _totalQueries = 0
    private _totalWrites = 0
    private _lastOpMs = 0
    private _inTransaction = false
    private _txType: 'deferred' | 'immediate' | undefined = undefined
    private _preparedCount = 0

    constructor(
        handle: SqliteEngineHandle,
        label: string,
        private readonly onClosed?: (conn: InProcessConnection) => void,
    ) {
        this.handle = handle
        this._label = label
    }

    get isOpen(): boolean { return this._isOpen }

    get pageCount(): number {
        return this.handle.databaseAdapter?.getPageCount() ?? 0
    }

    async execute<T = Record<string, unknown>>(
        sql: string,
        params?: SqlParam[],
        options?: QueryOptions,
    ): Promise<SqliteStatementResult<T>> {
        return this.enqueueConnectionOperation(async () => {
        this.assertOpen()
        options?.signal?.throwIfAborted()
        const t0 = performance.now()
        const managedCheckpoint = createManagedWalCheckpointResult<T>(sql, params)
        if (managedCheckpoint) {
            this.recordStatementMetrics(managedCheckpoint, t0)
            logSqlStatement({
                label: this._label,
                sql,
                paramsCount: params?.length ?? 0,
                durationMs: performance.now() - t0,
                rowCount: managedCheckpoint.rows.length,
                changes: managedCheckpoint.changes,
                ok: true,
            })
            return managedCheckpoint
        }

        try {
            const rows = await withBusyRetry(() => this.handle.query<T>(sql, params as unknown[]))
            const result = {
                rows,
                ...readStatementMetadata(this.handle),
            }
            this.recordStatementMetrics(result, t0)
            this.applyExplicitTransactionState(sql)
            logSqlStatement({
                label: this._label,
                sql,
                paramsCount: params?.length ?? 0,
                durationMs: performance.now() - t0,
                rowCount: result.rows.length,
                changes: result.changes,
                ok: true,
            })
            return result
        } catch (e: any) {
            this.recordStatementFailure(t0)
            const error = this.wrapError(e)
            logSqlStatement({
                label: this._label,
                sql,
                paramsCount: params?.length ?? 0,
                durationMs: performance.now() - t0,
                ok: false,
                error,
            })
            throw error
        }
        })
    }

    async query<T = Record<string, unknown>>(
        sql: string,
        params?: SqlParam[],
        options?: QueryOptions,
    ): Promise<T[]> {
        return (await this.execute<T>(sql, params, options)).rows
    }

    async exec(
        sql: string,
        params?: SqlParam[],
        options?: QueryOptions,
    ): Promise<ExecResult> {
        const result = await this.execute(sql, params, options)
        if (result.changes === 0) {
            this.recordZeroChangeWrite()
        }
        return {
            changes: result.changes,
            lastInsertRowId: result.lastInsertRowId,
        }
    }

    async *each<T = Record<string, unknown>>(
        sql: string,
        params?: SqlParam[],
        options?: QueryOptions,
    ): AsyncIterable<T> {
        this.assertOpen()
        options?.signal?.throwIfAborted()
        // For now, execute full query and yield rows
        // Phase 5+: streaming via prepared statement step()
        const rows = await this.query<T>(sql, params, options)
        for (const row of rows) {
            options?.signal?.throwIfAborted()
            yield row
        }
    }

    async prepare<T = Record<string, unknown>>(sql: string): Promise<PreparedStatement<T>> {
        this.assertOpen()
        // Prepared statement wrapper — reuses handle for repeated execution
        const handle = this.handle
        const conn = this
        let finalized = false
        conn._preparedCount++

        return {
            async all(params?: SqlParam[]): Promise<T[]> {
                if (finalized) throw new Error('Statement already finalized')
                return await withBusyRetry(() => handle.query<T>(sql, params as unknown[]))
            },
            async get(params?: SqlParam[]): Promise<T | null> {
                if (finalized) throw new Error('Statement already finalized')
                const rows = await withBusyRetry(() => handle.query<T>(sql, params as unknown[]))
                return rows[0] ?? null
            },
            async run(params?: SqlParam[]): Promise<ExecResult> {
                if (finalized) throw new Error('Statement already finalized')
                await withBusyRetry(() => handle.query(sql, params as unknown[]))
                return readStatementMetadata(handle)
            },
            async finalize(): Promise<void> {
                if (!finalized) { finalized = true; conn._preparedCount-- }
            },
            async [Symbol.asyncDispose](): Promise<void> {
                if (!finalized) { finalized = true; conn._preparedCount-- }
            },
        }
    }

    async transaction<T>(
        fn: (tx: SqliteTransaction) => Promise<T>,
        options?: TransactionOptions,
    ): Promise<T> {
        return this.enqueueConnectionOperation(async () => {
        this.assertOpen()
        // BEGIN IMMEDIATE for write transactions (prevents WAL deadlock on lock upgrade)
        const isImmediate = !!options?.immediate
        const begin = isImmediate ? 'BEGIN IMMEDIATE' : 'BEGIN'
        this._inTransaction = true
        this._txType = isImmediate ? 'immediate' : 'deferred'

        try {
            await withBusyRetry(() => this.handle.exec(begin))
        } catch (e: any) {
            this._inTransaction = false
            this._txType = undefined
            throw this.wrapError(e)
        }

        // Transaction proxy — same connection, within active transaction
        const handle = this.handle
        const wrapError = this.wrapError.bind(this)
        const recordStatementMetrics = this.recordStatementMetrics.bind(this)
        const recordStatementFailure = this.recordStatementFailure.bind(this)
        const recordZeroChangeWrite = this.recordZeroChangeWrite.bind(this)
        const label = this._label
        const tx: SqliteTransaction = {
            async execute<T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]): Promise<SqliteStatementResult<T2>> {
                const t0 = performance.now()
                try {
                    const rows = handle.query<T2>(sql, params as unknown[])
                    const result = {
                        rows,
                        ...readStatementMetadata(handle),
                    }
                    recordStatementMetrics(result, t0)
                    logSqlStatement({
                        label,
                        sql,
                        paramsCount: params?.length ?? 0,
                        durationMs: performance.now() - t0,
                        rowCount: result.rows.length,
                        changes: result.changes,
                        ok: true,
                    })
                    return result
                } catch (e: any) {
                    recordStatementFailure(t0)
                    const error = wrapError(e)
                    logSqlStatement({
                        label,
                        sql,
                        paramsCount: params?.length ?? 0,
                        durationMs: performance.now() - t0,
                        ok: false,
                        error,
                    })
                    throw error
                }
            },
            async query<T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]): Promise<T2[]> {
                try {
                    return (await tx.execute<T2>(sql, params)).rows
                } catch (e: any) { throw wrapError(e) }
            },
            async exec(sql: string, params?: SqlParam[]): Promise<ExecResult> {
                try {
                    const result = await tx.execute(sql, params)
                    if (result.changes === 0) {
                        recordZeroChangeWrite()
                    }
                    return {
                        changes: result.changes,
                        lastInsertRowId: result.lastInsertRowId,
                    }
                } catch (e: any) { throw wrapError(e) }
            },
            async *each<T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]): AsyncIterable<T2> {
                const rows = await tx.query<T2>(sql, params)
                for (const row of rows) yield row
            },
        }

        let commitAttempted = false
        try {
            const result = await fn(tx)
            commitAttempted = true
            await withBusyRetry(() => this.handle.exec('COMMIT'))
            this._inTransaction = false
            this._txType = undefined
            return result
        } catch (e) {
            if (commitAttempted) {
                await recoverAfterFailedCommit(e, {
                    execRollback: () => withBusyRetry(() => this.handle.exec('ROLLBACK')),
                })
            } else {
                try { await withBusyRetry(() => this.handle.exec('ROLLBACK')) } catch { /* ignore rollback errors */ }
            }
            this._inTransaction = false
            this._txType = undefined
            throw e
        }
        })
    }

    async close(): Promise<void> {
        if (!this._isOpen) return
        this._isOpen = false
        try {
            this.handle.close()
        } finally {
            this.onClosed?.(this)
        }
    }

    async [Symbol.asyncDispose](): Promise<void> {
        await this.close()
    }

    // §93 §13.1: Per-connection diagnostic snapshot
    diagnostics(): ConnectionDiag {
        return {
            label: this._label,
            isOpen: this._isOpen,
            inTransaction: this._inTransaction,
            transactionType: this._txType,
            preparedStatementCount: this._preparedCount,
            lastOperationMs: this._lastOpMs,
            totalQueries: this._totalQueries,
            totalWrites: this._totalWrites,
        }
    }

    /** Internal: get the raw handle for low-level access. */
    get _handle(): SqliteEngineHandle { return this.handle }

    private assertOpen(): void {
        if (!this._isOpen) throw new Error('Connection is closed')
    }

    private enqueueConnectionOperation<T>(operation: () => Promise<T>): Promise<T> {
        this.assertOpen()
        const previous = this.operationTail
        let release!: () => void
        this.operationTail = new Promise<void>(resolve => {
            release = resolve
        })
        return previous.catch(() => {}).then(async () => {
            try {
                this.assertOpen()
                return await operation()
            } finally {
                release()
            }
        })
    }

    private recordStatementMetrics(
        result: Pick<SqliteStatementResult, 'changes'>,
        startedAt: number,
    ): void {
        this._lastOpMs = performance.now() - startedAt
        this._totalQueries++
        if (result.changes > 0) {
            this._totalWrites++
        }
    }

    private recordZeroChangeWrite(): void {
        this._totalWrites++
    }

    private recordStatementFailure(startedAt: number): void {
        this._lastOpMs = performance.now() - startedAt
    }

    private applyExplicitTransactionState(sql: string): void {
        const nextState = getExplicitTransactionState(sql)
        if (!nextState) {
            return
        }

        this._inTransaction = nextState.inTransaction
        this._txType = nextState.transactionType
    }

    private wrapError(e: any): Error {
        // sqlite3.wasm errors have resultCode property
        if (e && typeof e.resultCode === 'number') {
            const code = e.resultCode
            const codeName = code === 5 ? 'SQLITE_BUSY'
                : code === 8 ? 'SQLITE_READONLY'
                    : code === 13 ? 'SQLITE_FULL'
                        : code === 19 ? 'SQLITE_CONSTRAINT'
                            : `SQLITE_${code}`

            // §Diag: Snapshot VFS state at error time for debugging
            try {
                const mem = this.handle.memoryManager
                const vfs = this.handle.vfs
                const ps = mem.pageSize
                const walData = mem.walDataSize
                const frameSize = WAL_FRAME_HDR_SIZE + ps
                const frames = walData > WAL_HDR_SIZE
                    ? Math.floor((walData - WAL_HDR_SIZE) / frameSize) : 0
                const sab2MaxBytes = mem.sab2 instanceof SharedArrayBuffer
                    ? sab2MaxByteLength(mem.sab2)
                    : mem.sab2.byteLength

                sdkConsole.error(
                    `[SecureBlob] SQL Error: ${codeName} (rc=${code})`,
                    `\n  WAL: ${(walData / 1024 / 1024).toFixed(1)}MB, ${frames} frames`,
                    `\n  SAB₂: current=${(mem.sab2.byteLength / 1024 / 1024).toFixed(1)}MB, max=${(sab2MaxBytes / 1024 / 1024).toFixed(1)}MB`,
                    `\n  Page: size=${ps}, dbPages=${mem.dbPageCount}, dirty=${mem.dirtyPageCount}`,
                    `\n  SAB mode: ${mem.isSabMode}`,
                    `\n  Connection: ${this._label}`,
                    `\n  Stats: queries=${this._totalQueries}, writes=${this._totalWrites}`,
                )
            } catch { /* diagnostics capture is non-fatal */ }

            return new SqliteError(e.message || String(e), code, codeName)
        }
        return e instanceof Error ? e : new Error(String(e))
    }
}

// ── RealSqliteDatabase ──────────────────────────────────────────

class RealSqliteDatabase implements SqliteDatabase {
    private _isOpen = true
    private runtimeOwner: SqliteEngineHandle
    private connections: SqliteConnection[] = []
    private nextConnectionLabelId = 0
    private _syncMode: SyncMode = 'concurrent' as SyncMode
    private syncListeners: Set<(event: SyncEvent) => void> = new Set()
    private diagListeners: Set<(diag: DatabaseDiagnostics) => void> = new Set()
    private diagTimer: ReturnType<typeof setInterval> | null = null
    private lastDiagJson = ''
    private engineSyncUnsubscribe: (() => void) | null = null
    private _workspace: string

    // §93 §13.1 SyncDiag — mutable state tracked across sync lifecycle
    private _syncState: 'idle' | 'starting' | 'syncing' | 'merging' | 'pushing' | 'pulling' | 'checkpointing' | 'stopped' | 'error' = 'idle'
    private _syncCycleCount = 0
    private _lastSyncResult: SyncResult | null = null
    private _lastSyncError: Error | null = null
    private _lastSyncAt: number | null = null
    private _nextSyncAt: number | null = null
    private _remoteEpoch = 0
    private _localEpoch = 0
    private _casRetryCount = 0
    private _lastConflictCount = 0
    private _leaseId: string | null = null
    private _leaseTtlRemainingMs: number | null = null

    constructor(
        runtimeOwner: SqliteEngineHandle,
        config: DatabaseConfig,
        private readonly engineFactory: SqliteEngineFactory,
    ) {
        this.runtimeOwner = runtimeOwner
        this._workspace = config.workspace ?? ''
        if (config.sync?.preferredMode) this._syncMode = config.sync.preferredMode
    }

    get syncMode(): SyncMode { return this._syncMode }
    get isOpen(): boolean { return this._isOpen }
    get connectionCount(): number { return this.connections.filter(c => c.isOpen).length }

    async connect(options?: ConnectionOptions): Promise<SqliteConnection> {
        this.assertOpen()
        const label = options?.label ?? `conn-${this.nextConnectionLabelId++}`

        const nodeWorkerThreads = await loadNodeWorkerThreads()
        if (nodeWorkerThreads != null) {
            try {
                return await this.connectViaWorkerThread(label, nodeWorkerThreads)
            } catch (error) {
                if (!isExternalAttachmentUnsupportedError(error)) {
                    throw error
                }
            }
        }

        // The runtime-owner handle is internal and is never returned to SDK
        // callers. Every public connection is a homogeneous client connection
        // assembled through openClient().
        const handle = await this.engineFactory.openClient(this.runtimeOwner)
        const conn = new InProcessConnection(handle, label, closedConn => {
            this.connections = this.connections.filter(connCandidate => connCandidate !== closedConn)
            this.tickDiagnostics()
        })
        this.connections.push(conn)
        this.tickDiagnostics()
        return conn
    }

    private async flushLocalInternal(options?: SqliteFlushLocalOptions): Promise<void> {
        if (this.databaseAdapter != null) {
            await this.databaseAdapter.flushLocal(options)
            this.tickDiagnostics()
            return
        }
    }

    private async flushRemoteInternal(): Promise<void> {
        if (this.databaseAdapter != null) {
            await this.databaseAdapter.flushRemote()
            this.tickDiagnostics()
            return
        }
        await this.syncNow()
    }

    async flushLocal(options?: SqliteFlushLocalOptions): Promise<void> {
        this.assertOpen()
        await this.flushLocalInternal(options)
    }

    async flushRemote(): Promise<void> {
        this.assertOpen()
        await this.flushRemoteInternal()
    }

    async close(options?: SqliteCloseOptions): Promise<void> {
        if (!this._isOpen) return
        this._isOpen = false

        // Stop diagnostic polling
        this.stopDiagPolling()
        this.stopEngineSyncSubscription(true)

        let closeError: unknown = null
        if (options?.flushRemote === true) {
            try {
                await this.flushRemoteInternal()
            } catch (error) {
                closeError = error
            }
        }

        // Close all connections (reverse order — secondaries first)
        const openConnections = [...this.connections]
        for (let i = openConnections.length - 1; i >= 0; i--) {
            try { await openConnections[i].close() } catch { /* ignore */ }
        }
        this.connections = []

        try {
            if (options?.flushRemote !== true && options?.flushLocal !== false) {
                await this.flushLocalInternal()
            }
        } catch (error) {
            closeError = error
        }

        try {
            this.runtimeOwner.close()
        } catch (error) {
            closeError ??= error
        }

        if (closeError != null) {
            throw closeError
        }
    }

    async setSyncMode(mode: SyncMode): Promise<void> {
        const prev = this._syncMode
        if (this.databaseAdapter != null) {
            await this.databaseAdapter.setSyncMode(mode)
            this._syncMode = mode
            if (prev !== mode) {
                this.tickDiagnostics()
            }
            return
        }

        this._syncMode = mode
        if (prev !== mode) {
            this.emitSync({ type: 'mode-change', from: prev, to: mode, reason: 'manual' })
            this.tickDiagnostics()
        }
    }

    async startSync(options?: { interval?: number }): Promise<void> {
        if (this.databaseAdapter != null) {
            await this.databaseAdapter.startSync(options)
            this.tickDiagnostics()
            return
        }

        this._syncState = 'starting'
        this.tickDiagnostics()
        this._syncState = 'idle'
        this.tickDiagnostics()
    }

    async stopSync(): Promise<void> {
        if (this.databaseAdapter != null) {
            await this.databaseAdapter.stopSync()
            this.tickDiagnostics()
            return
        }

        this._syncState = 'stopped'
        this._nextSyncAt = null
        this.tickDiagnostics()
    }

    async syncNow(): Promise<SyncResult> {
        if (this.databaseAdapter != null) {
            const result = await this.databaseAdapter.syncNow()
            this.tickDiagnostics()
            return result
        }

        const t0 = performance.now()
        this._syncState = 'syncing'
        this._syncCycleCount++
        this.tickDiagnostics()

        const result: SyncResult = {
            direction: 'push',
            pagesChanged: 0,
            conflicts: [],
            durationMs: performance.now() - t0,
        }

        this._lastSyncResult = result
        this._lastSyncAt = Date.now()
        this._lastSyncError = null
        this._lastConflictCount = result.conflicts.length
        this._localEpoch++
        this._syncState = 'idle'
        this.emitSync({ type: 'sync-complete', result })
        this.tickDiagnostics()
        return result
    }

    // ── Internal: workspace-driven page re-import ────────────────

    /**
     * Re-import pages from remote sync (TB-scale safe).
     * Phase 5 WAL-native import:
     *   1. importMergedPages → updates SAB₁ page window (delta, O(k))
     *   2. appendWalFramesForSync → writes as WAL frames + bumps iChange
     *      → SQLite's pager auto-detects and re-reads fresh data
     * No close/reopen. Connections stay alive. O(changed pages).
     */
    reimport(pages: Map<number, Uint8Array>, _pageSize: number): void {
        if (!this._isOpen || pages.size === 0) return
        // 1. Update SAB₁ page window with merged pages (essential)
        this.runtimeOwner.importMergedPages(pages)
        // 2. Write as WAL frames → pager cache invalidation via iChange bump
        //    Best-effort: WAL may not be initialized on fresh DBs (no queries yet)
        try {
            this.runtimeOwner.appendWalFramesForSync(pages)
        } catch (err) {
            sdkConsole.warn('[SecureBlob:Sync] WAL frame append failed (pager cache stale until next query):', err)
        }
        this._remoteEpoch++
        this.emitSync({ type: 'remote-changes', changedPages: pages.size })
        this.tickDiagnostics()
    }

    onSync(callback: (event: SyncEvent) => void): () => void {
        this.syncListeners.add(callback)
        this.ensureEngineSyncSubscription()
        return () => {
            this.syncListeners.delete(callback)
            this.stopEngineSyncSubscription()
        }
    }

    /**
     * @deprecated Node.js runtime does not emit sqlite3mt debug-stage events.
     * This is a no-op stub to satisfy the SqliteDatabase interface.
     */
    setDebugStageListener(_listener: ((payload: unknown) => void) | null): void {
        // no-op — debug-stage is a browser sqlite3mt concept
    }

    // §93 §13.1: Full diagnostic snapshot — zero-allocation hot path
    diagnostics(): DatabaseDiagnostics {
        const mem = this.runtimeOwner.memoryManager
        const pageSize = mem.pageSize
        const walDataSize = mem.walDataSize
        const frameSize = WAL_FRAME_HDR_SIZE + pageSize
        const syncSnapshot = this.databaseAdapter?.getSyncDiagnosticsSnapshot() ?? {}

        // WAL metrics: compute from walDataSize
        const frameCount = walDataSize > WAL_HDR_SIZE
            ? Math.floor((walDataSize - WAL_HDR_SIZE) / frameSize)
            : 0

        // Read mxFrame from WAL-Index header in SAB₃ (offset 16, uint32 LE)
        // and lastCheckpointFrame (nBackfill at offset 96, uint32 LE)
        let mxFrame = frameCount
        let lastCheckpointFrame = 0
        const shmRegion = mem.shmRegion(0, WALINDEX_PGSZ)
        if (shmRegion && shmRegion.byteLength >= 136) {
            const shmView = new DataView(shmRegion.buffer, shmRegion.byteOffset, shmRegion.byteLength)
            // WalIndexHdr: mxFrame at offset 16 (little-endian u32)
            mxFrame = shmView.getUint32(16, true) || frameCount
            // WalCkptInfo: nBackfill at offset 96 (little-endian u32)
            lastCheckpointFrame = shmView.getUint32(96, true)
        }

        // Lock state: read from Atomics on SAB₃ lock region
        const locks = this.readLockState()
        const syncState = syncSnapshot.state ?? this._syncState
        const isSyncing = syncSnapshot.isSyncing ?? (
            syncState === 'syncing'
            || syncState === 'merging'
            || syncState === 'pushing'
            || syncState === 'pulling'
        )

        const sync = {
            timestamp: performance.now(),
            workspace: this._workspace,
            memory: {
                sab1Size: mem.sab1.byteLength,
                sab2Size: mem.sab2.byteLength,
                sab3Size: mem.sab3.byteLength,
                totalSabBytes: mem.sab1.byteLength + mem.sab2.byteLength + mem.sab3.byteLength,
                pageWindowSlots: mem.pageWindowSlots,
                pageWindowUsed: mem.pageWindowUsed,
                dirtyPageCount: mem.dirtyPageCount,
                pageSize,
                dbPageCount: mem.dbPageCount,
                sabSupported: mem.isSabMode,
            },
            wal: {
                mxFrame,
                walDataSize,
                frameCount,
                lastCheckpointFrame,
                framesSinceCheckpoint: mxFrame - lastCheckpointFrame,
                autoCheckpointThreshold: mem.autoCheckpointThreshold ?? 1000,
            },
            connections: this.connections
                .filter(connection => connection.isOpen)
                .map(connection => connection.diagnostics()),
            sync: {
                mode: syncSnapshot.mode ?? this._syncMode,
                state: syncState,
                isSyncing,
                cycleCount: syncSnapshot.cycleCount ?? this._syncCycleCount,
                lastResult: syncSnapshot.lastResult ?? this._lastSyncResult,
                lastError: syncSnapshot.lastError ?? this._lastSyncError,
                lastSyncAt: syncSnapshot.lastSyncAt ?? this._lastSyncAt,
                nextSyncAt: syncSnapshot.nextSyncAt ?? this._nextSyncAt,
                pendingPushPages: syncSnapshot.pendingPushPages ?? mem.dirtyPageCount,
                remoteEpoch: syncSnapshot.remoteEpoch ?? this._remoteEpoch,
                localEpoch: syncSnapshot.localEpoch ?? this._localEpoch,
                casRetryCount: syncSnapshot.casRetryCount ?? this._casRetryCount,
                lastConflictCount: syncSnapshot.lastConflictCount ?? this._lastConflictCount,
                leaseId: syncSnapshot.leaseId ?? this._leaseId,
                leaseTtlRemainingMs: syncSnapshot.leaseTtlRemainingMs ?? this._leaseTtlRemainingMs,
            },
            locks,
            drain: {
                mode0Active: mem.isMode0Active(),
                drainPending: mem.isDrainPending(),
                transactionsBlocked: mem.isMode0Active() || mem.isDrainPending(),
                initiator: null,
                drainStartedAt: null,
                drainTimeoutAt: null,
            },
        }

        if ('debugInternal' in (syncSnapshot as Record<string, unknown>)) {
            ;(sync.sync as SyncDiag & { debugInternal?: unknown }).debugInternal =
                (syncSnapshot as SyncDiag & { debugInternal?: unknown }).debugInternal
        }

        return sync
    }

    // §93 §13.3: Diagnostic subscription — max 10Hz, fires on change
    onDiagnostics(callback: (diag: DatabaseDiagnostics) => void): () => void {
        this.diagListeners.add(callback)

        // Start polling if first subscriber
        if (this.diagListeners.size === 1 && !this.diagTimer) {
            this.lastDiagJson = ''
            this.diagTimer = setInterval(() => this.pollDiagnostics(), 100)
        }

        return () => {
            this.diagListeners.delete(callback)
            if (this.diagListeners.size === 0) {
                this.stopDiagPolling()
            }
        }
    }

    async [Symbol.asyncDispose](): Promise<void> {
        await this.close()
    }

    /** Internal: get the runtime-owner handle (for testing, diagnostics). */
    get _runtimeOwner(): SqliteEngineHandle { return this.runtimeOwner }

    // ── Private helpers ─────────────────────────────────────────

    private assertOpen(): void {
        if (!this._isOpen) throw new Error('Database is closed')
    }

    private async connectViaWorkerThread(
        label: string,
        workerThreads: NodeWorkerThreadsModule,
    ): Promise<SqliteConnection> {
        const attach = reserveSqlite3MtClientAttachment(this.engineFactory)
        const channel = new workerThreads.MessageChannel()
        const onControlMessage = (message: BrowserSqliteClientToControlMessage): void => {
            if (!message || typeof message !== 'object' || typeof message.type !== 'string') {
                return
            }

            if (message.type === 'client-io-fatal') {
                enterSqlite3MtExternalClientIoFatal(this.engineFactory, attach.connId)
                return
            }

            if (message.type === 'client-request-start') {
                enterSqlite3MtExternalClientRequest(this.engineFactory, message.connId, message.runtimeEpoch)
                return
            }

            if (message.type === 'client-request-end') {
                leaveSqlite3MtExternalClientRequest(this.engineFactory, message.connId, message.runtimeEpoch)
                return
            }

            if (isSqlite3MtDispatchableClientMessage(message)) {
                dispatchSqlite3MtExternalMessage(this.engineFactory, message)
            }
        }
        channel.port1.on('message', onControlMessage)

        let released = false
        const releaseResources = () => {
            if (released) {
                return
            }

            released = true
            channel.port1.off('message', onControlMessage)
            channel.port1.close()
            releaseSqlite3MtClientAttachment(this.engineFactory, attach.connId)
        }

        const componentWorkerUrl = resolveComponentNodeSqliteWorkerUrl()
        const workerUrl = componentWorkerUrl ?? new URL('./realSqlite.client.worker.ts', import.meta.url)
        const worker = new workerThreads.Worker(
            workerUrl,
            componentWorkerUrl == null ? buildNodeWorkerOptions(workerUrl) : undefined,
        )
        const rpc = new NodeWorkerRpc<BrowserSqliteClientWorkerRequest>(worker)

        try {
            await rpc.request<void>(
                {
                    op: 'init-connection',
                    attach,
                    port: channel.port2 as unknown as MessagePort,
                },
                [channel.port2],
            )
        } catch (error) {
            releaseResources()
            await rpc.terminate()
            throw error
        }

        let conn: WorkerThreadConnection
        conn = new WorkerThreadConnection(
            rpc,
            attach.connId,
            createConnectionDiag(label),
            () => {
                releaseResources()
                this.connections = this.connections.filter(connCandidate => connCandidate !== conn)
                this.tickDiagnostics()
            },
        )
        this.connections.push(conn)
        this.tickDiagnostics()
        return conn
    }

    private get databaseAdapter(): SqliteEngineDatabaseAdapter | null {
        return this.runtimeOwner.databaseAdapter ?? null
    }

    private emitSync(event: SyncEvent): void {
        for (const cb of this.syncListeners) {
            try { cb(event) } catch { /* listener errors are silenced */ }
        }
    }

    private observeSyncEvent(event: SyncEvent): void {
        switch (event.type) {
            case 'sync-start':
                this._syncState = 'syncing'
                break
            case 'sync-complete':
                this._syncState = 'idle'
                this._syncCycleCount++
                this._lastSyncResult = event.result
                this._lastSyncError = null
                this._lastSyncAt = Date.now()
                this._lastConflictCount = event.result.conflicts.length
                this._localEpoch++
                break
            case 'sync-error':
                this._syncState = 'error'
                this._lastSyncError = event.error
                break
            case 'remote-changes':
                this._remoteEpoch++
                break
            case 'mode-change':
                this._syncMode = event.to
                break
            case 'drain-requested':
            case 'drain-complete':
            case 'lock-escalation':
                break
        }
    }

    private ensureEngineSyncSubscription(): void {
        if (this.syncListeners.size === 0) return
        if (this.engineSyncUnsubscribe != null) return
        if (this.databaseAdapter == null) return

        this.engineSyncUnsubscribe = this.databaseAdapter.onSync(event => {
            this.observeSyncEvent(event)
            this.emitSync(event)
            this.tickDiagnostics()
        })
    }

    private stopEngineSyncSubscription(force = false): void {
        if (!force && this.syncListeners.size > 0) return
        if (this.engineSyncUnsubscribe == null) return

        this.engineSyncUnsubscribe()
        this.engineSyncUnsubscribe = null
    }

    /** Read lock state from SAB₃ Atomics lock view. */
    private readLockState() {
        const mem = this.runtimeOwner.memoryManager
        const lv = mem.lockView
        const isSab = mem.isSabMode

        // Read each slot (Atomics.load for SAB, plain read for ArrayBuffer)
        const readSlot = (i: number) => isSab
            ? Atomics.load(lv, i)
            : lv[i]

        const writeLock = readSlot(0)
        const checkpointLock = readSlot(1)
        const recoverLock = readSlot(2)

        return {
            writeLock,
            checkpointLock,
            recoverLock,
            readLocks: [readSlot(3), readSlot(4), readSlot(5), readSlot(6), readSlot(7)] as readonly [number, number, number, number, number],
            isWriteLocked: writeLock < 0,
            importPending: mem.isImportPending(),
        }
    }

    /** Notify diagnostic subscribers if state changed (debounced via tick). */
    private tickDiagnostics(): void {
        if (this.diagListeners.size === 0) return
        // Actual delivery happens via the polling interval (max 10Hz)
    }

    /** 100ms polling — compare snapshot JSON, fire on change. */
    private pollDiagnostics(): void {
        if (this.diagListeners.size === 0) return
        try {
            const diag = this.diagnostics()
            const json = JSON.stringify(diag)
            if (json !== this.lastDiagJson) {
                this.lastDiagJson = json
                for (const cb of this.diagListeners) {
                    try { cb(diag) } catch { /* listener errors silenced */ }
                }
            }
        } catch { /* diagnostics read failure is non-fatal */ }
    }

    private stopDiagPolling(): void {
        if (this.diagTimer) {
            clearInterval(this.diagTimer)
            this.diagTimer = null
        }
        this.diagListeners.clear()
    }
}

// ── Factory ─────────────────────────────────────────────────────

/**
 * Open a database. Loads WASM, allocates SABs, registers VFS.
 * Returns SqliteDatabase with connect() for multi-connection WAL concurrency.
 *
 * @example
 * ```ts
 * await using db = await openDatabase({ workspace: 'invoices' })
 * await using conn = await db.connect()
 * const rows = await conn.query<Invoice>('SELECT * FROM invoices')
 *
 * // Observability: render sync status in your UI
 * const diag = db.diagnostics()
 * statusBar.show(`WAL: ${diag.wal.frameCount} frames, Sync: ${diag.sync.state}`)
 * ```
 */
export async function openDatabaseWithEngineFactory(
    config: DatabaseConfig,
    engineFactory: SqliteEngineFactory,
): Promise<SqliteDatabase> {
    const runtimeOwner = await engineFactory.openRuntimeOwner(
        toSqliteEngineOpenConfig(config)
    )
    return new RealSqliteDatabase(runtimeOwner, config, engineFactory)
}
